

import { MindMapNode, NodeLink, NodeWidth } from "../types";

export type LayoutType = 'free' | 'horizontal' | 'horizontal-right' | 'vertical' | 'vertical-both';

interface TreeNode {
  id: string;
  width: number;
  height: number;
  children: TreeNode[];
  x: number;
  y: number;
}

// Spacing constants
const SIBLING_SEPARATION = 120; // Gap between siblings
const VERTICAL_LEVEL_GAP = 150; // Vertical gap between ranks in Vertical mode
const MAX_TEXT_DISPLAY_HEIGHT = 320; // Must match the value in NodeCard.tsx

const getBaseWidth = (nodeWidth: NodeWidth) => {
    switch (nodeWidth) {
        case 'wide': return 500; // 480px + margin
        case 'extra-wide': return 660; // 640px + margin
        default: return 340; // 320px + margin
    }
};

// Helper to estimate actual rendered size of a node
const estimateNodeDimensions = (node: MindMapNode, nodeWidth: NodeWidth, imageExpanded = false, nodeExpandCycle?: 0 | 1 | 2, actualHeight?: number): { width: number; height: number } => {
  const baseWidth = getBaseWidth(nodeWidth);

  // Use actual measured DOM height when available — avoids estimation errors
  if (actualHeight !== undefined && actualHeight > 0) {
    return { width: baseWidth, height: actualHeight + 20 }; // +20 safety margin
  }

  // Default to true if undefined for backward compatibility
  const isExpanded = (nodeExpandCycle !== undefined ? nodeExpandCycle >= 1 : node.isExpanded !== false);
  const effectiveImageExpanded = imageExpanded || nodeExpandCycle === 2;

  // Base Height (Header + Footer + Pads)
  let height = 120;

  // IMAGES (Always visible now)
  if (node.imageBase64 || node.drawingBase64 || (node.attachments && node.attachments.length > 0)) {
     height += effectiveImageExpanded ? 580 : 240;
  }

  if (isExpanded) {
    // TEXT CONTENT ESTIMATION
    if (node.content && node.content.length > 0) {
        // Estimate chars per line based on width. 
        const charsPerLine = nodeWidth === 'wide' ? 50 : nodeWidth === 'extra-wide' ? 70 : 32;
        
        const newLines = (node.content.match(/\n/g) || []).length;
        const wrappedLines = Math.ceil(node.content.length / charsPerLine);
        const totalLines = Math.max(wrappedLines, newLines + 1);
        const extraLines = Math.max(0, totalLines - 3);
        
        // Calculate potential text height
        let textHeight = 80 + (extraLines * 24);
        
        // CAP the height contribution to layout calculation
        textHeight = Math.min(textHeight, MAX_TEXT_DISPLAY_HEIGHT);
        
        height += textHeight; 
    } else {
        height += 50; 
    }

    // FRAMEWORK
    if (node.suggestedQuestions?.length) {
        height += node.suggestedQuestions.length * 30 + 40;
    }

    // AI ANALYSIS / FEEDBACK
    if (node.analysis) {
        height += 60; // Score bar
        const feedbackLen = node.analysis.feedback?.length || 0;
        const feedbackLines = Math.ceil(feedbackLen / (nodeWidth === 'wide' ? 55 : 35));
        height += Math.max(60, feedbackLines * 20);
    }
  } else {
      // COLLAPSED TEXT SNIPPET
      height += 50; 
  }

  // REVIEW QUESTIONS BUTTON
  if (node.reviewQuestionCount && node.reviewQuestionCount > 0) {
      height += 40; // "X câu hỏi ôn tập" button row
  }

  // RELATED LINKS & BUFFER
  height += 30; // Chips
  height += 40; // Safety buffer

  return { width: baseWidth, height };
};

/**
 * Main function to apply layout to a set of nodes and links
 */
export const calculateLayout = (
  nodes: MindMapNode[],
  links: NodeLink[],
  type: LayoutType,
  nodeWidth: NodeWidth = 'standard',
  imageExpanded = false,
  nodeExpandCycles?: Record<string, 0 | 1 | 2>,
  actualHeights?: Record<string, number>
): MindMapNode[] => {
  if (type === 'free' || nodes.length === 0) return nodes;
  const isHorizontal = type === 'horizontal' || type === 'horizontal-right';
  const isVertical = type === 'vertical' || type === 'vertical-both';

  // 1. Build Tree Structure
  const nodeMap = new Map<string, MindMapNode>();
  nodes.forEach(n => nodeMap.set(n.id, n));

  const childrenMap = new Map<string, string[]>();
  const parentMap = new Map<string, string>();

  // Initialize maps
  nodes.forEach(n => childrenMap.set(n.id, []));

  // Populate relationships based strictly on PARENT ID (Structural Hierarchy)
  // We ignore manual 'links' for layout purposes, treating them as visual-only connections.
  nodes.forEach(node => {
    if (node.parentId && nodeMap.has(node.parentId)) {
       childrenMap.get(node.parentId)?.push(node.id);
       parentMap.set(node.id, node.parentId);
    }
  });

  // Find Roots (nodes that have no parent, or their parent doesn't exist in the set)
  const roots = nodes.filter(n => !n.parentId || !nodeMap.has(n.parentId));
  const rootIds = roots.length > 0 ? roots.map(r => r.id) : [nodes[0].id];

  const buildTree = (id: string): TreeNode => {
    const childrenIds = childrenMap.get(id) || [];
    const node = nodeMap.get(id);
    const dims = node ? estimateNodeDimensions(node, nodeWidth, imageExpanded, nodeExpandCycles?.[id] as 0|1|2|undefined, actualHeights?.[id]) : { width: 340, height: 250 };

    return {
      id,
      width: dims.width,
      height: dims.height,
      children: childrenIds.map(buildTree),
      x: 0,
      y: 0
    };
  };

  const layoutedNodes: MindMapNode[] = [...nodes];
  let forestOffset = 0;

  // Pre-calculate per-level max heights for Vertical Layout
  // This map stores: depth -> max_height_of_any_node_at_this_depth
  const levelMaxHeights = new Map<number, number>();

  if (isVertical) {
    // Helper to traverse and fill max heights
    const scanLevelHeights = (node: TreeNode, depth: number) => {
        const currentMax = levelMaxHeights.get(depth) || 0;
        levelMaxHeights.set(depth, Math.max(currentMax, node.height));
        node.children.forEach(c => scanLevelHeights(c, depth + 1));
    };

    rootIds.forEach(rootId => {
        const treeRoot = buildTree(rootId);
        scanLevelHeights(treeRoot, 0);
    });
  }

  // Calculate cumulative Y offsets for each level based on max heights
  const levelYOffsets = new Map<number, number>();
  if (isVertical) {
      let currentY = 0;
      // Assume reasonable max depth of 50 to prevent infinite loops if recursive issues exist
      for (let d = 0; d < 50; d++) {
          levelYOffsets.set(d, currentY);
          const h = levelMaxHeights.get(d);
          if (h === undefined) break; 
          currentY += h + VERTICAL_LEVEL_GAP;
      }
  }

  const levelSeparation = nodeWidth === 'wide' ? 550 : nodeWidth === 'extra-wide' ? 720 : 380;

  rootIds.forEach(rootId => {
    const treeRoot = buildTree(rootId);

    if (type === 'horizontal') {
      calculateHorizontalLayout(treeRoot);
      applyBidirectionalLayout(treeRoot, nodeMap, forestOffset, layoutedNodes, levelSeparation);
      const allChildren = treeRoot.children;
      const leftCount = Math.floor(allChildren.length / 2);
      const leftChildren = allChildren.slice(0, leftCount);
      const rightChildren = allChildren.slice(leftCount);
      const rBlockH = rightChildren.reduce((s, c) => s + c.height, 0) + Math.max(0, rightChildren.length - 1) * SIBLING_SEPARATION;
      const lBlockH = leftChildren.reduce((s, c) => s + c.height, 0) + Math.max(0, leftChildren.length - 1) * SIBLING_SEPARATION;
      forestOffset += Math.max(rBlockH, lBlockH, treeRoot.height) + SIBLING_SEPARATION;
    } else if (type === 'horizontal-right') {
      calculateHorizontalLayout(treeRoot);
      applyPositions(treeRoot, nodeMap, forestOffset, 'horizontal', layoutedNodes, 0, 0, null, levelSeparation);
      forestOffset += treeRoot.height + SIBLING_SEPARATION;
    } else if (type === 'vertical-both') {
      calculateVerticalLayout(treeRoot);
      applyBidirectionalVerticalLayout(treeRoot, nodeMap, forestOffset, layoutedNodes, levelSeparation);
      const allChildren = treeRoot.children;
      const topCount = Math.floor(allChildren.length / 2);
      const topChildren = allChildren.slice(0, topCount);
      const bottomChildren = allChildren.slice(topCount);
      const bBlockW = bottomChildren.reduce((s, c) => s + c.width, 0) + Math.max(0, bottomChildren.length - 1) * SIBLING_SEPARATION;
      const tBlockW = topChildren.reduce((s, c) => s + c.width, 0) + Math.max(0, topChildren.length - 1) * SIBLING_SEPARATION;
      forestOffset += Math.max(bBlockW, tBlockW, treeRoot.width) + SIBLING_SEPARATION;
    } else {
      calculateVerticalLayout(treeRoot);
      applyPositions(treeRoot, nodeMap, forestOffset, type, layoutedNodes, 0, 0, levelYOffsets, levelSeparation);
      forestOffset += treeRoot.width + SIBLING_SEPARATION;
    }
  });

  return layoutedNodes;
};

// --- ALGORITHMS ---

const calculateHorizontalLayout = (node: TreeNode) => {
  let childrenTotalHeight = 0;
  
  node.children.forEach(child => {
    calculateHorizontalLayout(child);
    childrenTotalHeight += child.height; 
  });

  if (node.children.length > 0) {
    childrenTotalHeight += (node.children.length - 1) * SIBLING_SEPARATION;
    // The node height for layout purposes is determined by its children's spread
    // But it must be at least the node's own height
    node.height = Math.max(node.height, childrenTotalHeight);
  }
};

const calculateVerticalLayout = (node: TreeNode) => {
    let childrenTotalWidth = 0;
    node.children.forEach(child => {
        calculateVerticalLayout(child);
        childrenTotalWidth += child.width;
    });

    if (node.children.length > 0) {
        childrenTotalWidth += (node.children.length - 1) * SIBLING_SEPARATION;
        node.width = Math.max(node.width, childrenTotalWidth);
    }
};

// Bidirectional horizontal layout: root at center, children split left/right
const applyBidirectionalLayout = (
  treeRoot: TreeNode,
  nodeMap: Map<string, MindMapNode>,
  forestOffset: number,
  layoutedNodes: MindMapNode[],
  levelSeparation: number
) => {
  const allChildren = treeRoot.children;
  const leftCount = Math.floor(allChildren.length / 2);
  const leftChildren = allChildren.slice(0, leftCount);
  const rightChildren = allChildren.slice(leftCount);

  const rBlockH = rightChildren.reduce((s, c) => s + c.height, 0) + Math.max(0, rightChildren.length - 1) * SIBLING_SEPARATION;
  const lBlockH = leftChildren.reduce((s, c) => s + c.height, 0) + Math.max(0, leftChildren.length - 1) * SIBLING_SEPARATION;
  const totalHeight = Math.max(rBlockH, lBlockH, treeRoot.height);

  // Root at center
  const rootIdx = layoutedNodes.findIndex(n => n.id === treeRoot.id);
  if (rootIdx !== -1) {
    layoutedNodes[rootIdx] = { ...layoutedNodes[rootIdx], position: { x: 0, y: forestOffset + totalHeight / 2 } };
  }

  // Right children
  let rCursor = (totalHeight - rBlockH) / 2;
  rightChildren.forEach(child => {
    applyDirectionalPositions(child, nodeMap, forestOffset, layoutedNodes, 1, rCursor, levelSeparation, 'right');
    rCursor += child.height + SIBLING_SEPARATION;
  });

  // Left children
  let lCursor = (totalHeight - lBlockH) / 2;
  leftChildren.forEach(child => {
    applyDirectionalPositions(child, nodeMap, forestOffset, layoutedNodes, 1, lCursor, levelSeparation, 'left');
    lCursor += child.height + SIBLING_SEPARATION;
  });
};

const applyDirectionalPositions = (
  node: TreeNode,
  nodeMap: Map<string, MindMapNode>,
  baseY: number,
  resultArray: MindMapNode[],
  depth: number,
  currentStart: number,
  levelSeparation: number,
  direction: 'left' | 'right'
) => {
  const x = direction === 'right' ? depth * levelSeparation : -(depth * levelSeparation);
  const y = baseY + currentStart + (node.height / 2);

  const idx = resultArray.findIndex(n => n.id === node.id);
  if (idx !== -1) {
    resultArray[idx] = { ...resultArray[idx], position: { x, y } };
  }

  let childrenBlockHeight = node.children.reduce((s, c) => s + c.height, 0)
    + Math.max(0, node.children.length - 1) * SIBLING_SEPARATION;
  let childYCursor = (node.height - childrenBlockHeight) / 2;

  node.children.forEach(child => {
    applyDirectionalPositions(child, nodeMap, baseY + currentStart, resultArray, depth + 1, childYCursor, levelSeparation, direction);
    childYCursor += child.height + SIBLING_SEPARATION;
  });
};

// Bidirectional vertical layout: root at center, children split top/bottom
const applyBidirectionalVerticalLayout = (
  treeRoot: TreeNode,
  nodeMap: Map<string, MindMapNode>,
  forestOffset: number,
  layoutedNodes: MindMapNode[],
  levelSeparation: number
) => {
  const allChildren = treeRoot.children;
  const topCount = Math.floor(allChildren.length / 2);
  const topChildren = allChildren.slice(0, topCount);
  const bottomChildren = allChildren.slice(topCount);

  const bBlockW = bottomChildren.reduce((s, c) => s + c.width, 0) + Math.max(0, bottomChildren.length - 1) * SIBLING_SEPARATION;
  const tBlockW = topChildren.reduce((s, c) => s + c.width, 0) + Math.max(0, topChildren.length - 1) * SIBLING_SEPARATION;
  const totalWidth = Math.max(bBlockW, tBlockW, treeRoot.width);

  const rootIdx = layoutedNodes.findIndex(n => n.id === treeRoot.id);
  if (rootIdx !== -1) {
    layoutedNodes[rootIdx] = { ...layoutedNodes[rootIdx], position: { x: forestOffset + totalWidth / 2, y: 0 } };
  }

  let bCursor = (totalWidth - bBlockW) / 2;
  bottomChildren.forEach(child => {
    applyDirectionalPositionsVertical(child, nodeMap, forestOffset, layoutedNodes, 1, bCursor, 'down');
    bCursor += child.width + SIBLING_SEPARATION;
  });

  let tCursor = (totalWidth - tBlockW) / 2;
  topChildren.forEach(child => {
    applyDirectionalPositionsVertical(child, nodeMap, forestOffset, layoutedNodes, 1, tCursor, 'up');
    tCursor += child.width + SIBLING_SEPARATION;
  });
};

const VERTICAL_BOTH_STEP = 400;

const applyDirectionalPositionsVertical = (
  node: TreeNode,
  nodeMap: Map<string, MindMapNode>,
  baseX: number,
  resultArray: MindMapNode[],
  depth: number,
  currentStart: number,
  direction: 'up' | 'down'
) => {
  const y = direction === 'down'
    ? depth * VERTICAL_BOTH_STEP + node.height / 2
    : -(depth * VERTICAL_BOTH_STEP + node.height / 2);
  const x = baseX + currentStart + (node.width / 2);

  const idx = resultArray.findIndex(n => n.id === node.id);
  if (idx !== -1) {
    resultArray[idx] = { ...resultArray[idx], position: { x, y } };
  }

  let childrenBlockWidth = node.children.reduce((s, c) => s + c.width, 0)
    + Math.max(0, node.children.length - 1) * SIBLING_SEPARATION;
  let childXCursor = (node.width - childrenBlockWidth) / 2;

  node.children.forEach(child => {
    applyDirectionalPositionsVertical(child, nodeMap, baseX + currentStart, resultArray, depth + 1, childXCursor, direction);
    childXCursor += child.width + SIBLING_SEPARATION;
  });
};

const applyPositions = (
  node: TreeNode,
  nodeMap: Map<string, MindMapNode>,
  offset: number,
  type: LayoutType,
  resultArray: MindMapNode[],
  depth: number = 0,
  currentStart: number = 0,
  levelYOffsets: Map<number, number> | null,
  levelSeparation: number
) => {
  const originalNode = nodeMap.get(node.id);
  if (!originalNode) return;

  if (type === 'horizontal') {
    const x = depth * levelSeparation;
    const y = offset + currentStart + (node.height / 2);

    // 🔥 FIX: Only update position, don't replace entire node
    const idx = resultArray.findIndex(n => n.id === node.id);
    if (idx !== -1) {
      resultArray[idx] = { ...resultArray[idx], position: { x, y } };
    }

    let childrenBlockHeight = 0;
    node.children.forEach(c => childrenBlockHeight += c.height);
    childrenBlockHeight += Math.max(0, node.children.length - 1) * SIBLING_SEPARATION;

    // Center the children block relative to the parent
    let childYCursor = (node.height - childrenBlockHeight) / 2;

    node.children.forEach(child => {
      applyPositions(child, nodeMap, offset + currentStart, type, resultArray, depth + 1, childYCursor, null, levelSeparation);
      childYCursor += child.height + SIBLING_SEPARATION;
    });

  } else {
    // VERTICAL LAYOUT
    const topY = levelYOffsets?.get(depth) || (depth * 400);

    // node.height already has the correct estimated/measured height from buildTree
    const y = topY + (node.height / 2);

    const x = offset + currentStart + (node.width / 2);

    // 🔥 FIX: Only update position, don't replace entire node
    const idx = resultArray.findIndex(n => n.id === node.id);
    if (idx !== -1) {
      resultArray[idx] = { ...resultArray[idx], position: { x, y } };
    }

    let childrenBlockWidth = 0;
    node.children.forEach(c => childrenBlockWidth += c.width);
    childrenBlockWidth += Math.max(0, node.children.length - 1) * SIBLING_SEPARATION;

    // Center the children block relative to the parent
    let childXCursor = (node.width - childrenBlockWidth) / 2;

    node.children.forEach(child => {
        applyPositions(child, nodeMap, offset + currentStart, type, resultArray, depth + 1, childXCursor, levelYOffsets, levelSeparation);
        childXCursor += child.width + SIBLING_SEPARATION;
    });
  }
};
